import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { take } from 'rxjs';
import {
  ConfirmationRequest,
  DeviceAssociationInfo,
  DeviceAuthorizationResponse,
  DeviceCertificateApprovalBody,
  DeviceRegistrationRequest,
  DeviceRegistrationResponse,
  NonceResponse,
} from '../models/certificate.models';
import { CertificateService } from './certificate.service';

type CertificateState = {
  certificateInfo: DeviceAssociationInfo | null;
  searchResults: DeviceAssociationInfo[];
  nonceResponse: NonceResponse | null;
  registrationResponse: DeviceRegistrationResponse | null;
  authorizationResponse: DeviceAuthorizationResponse | null;
  associationConfirmed: boolean;
  loading: boolean;
  error: unknown | null;
};

const initialState: CertificateState = {
  certificateInfo: null,
  searchResults: [],
  nonceResponse: null,
  registrationResponse: null,
  authorizationResponse: null,
  associationConfirmed: false,
  loading: false,
  error: null,
};

export const CertificateStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasCertificateInfo: computed(() => store.certificateInfo() !== null),
    hasSearchResults: computed(() => store.searchResults().length > 0),
    busy: computed(() => store.loading()),
  })),
  withMethods((store, certificateService = inject(CertificateService)) => ({
    getDeviceCertificateInfoByPasscode(pinNumber: string): void {
      patchState(store, { loading: true, error: null });
      certificateService
        .getAssociationByConfirmationPin(pinNumber)
        .pipe(take(1))
        .subscribe({
          next: (certificateInfo) => {
            patchState(store, { certificateInfo, loading: false });
          },
          error: (error: unknown) => {
            patchState(store, { error, loading: false });
          },
        });
    },

    searchAssociationByDeviceIdSubstring(
      deviceIdSubString: string,
      limit: number = 5,
    ): void {
      patchState(store, { loading: true, error: null });
      certificateService
        .searchAssociationByDeviceIdSubstring(deviceIdSubString, limit)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            const searchResults =
              response.request ??
              (
                response as typeof response & {
                  requests?: DeviceAssociationInfo[];
                }
              ).requests ??
              [];
            patchState(store, {
              searchResults,
              loading: false,
            });
          },
          error: (error: unknown) => {
            patchState(store, { searchResults: [], error, loading: false });
          },
        });
    },

    clearSearchResults(): void {
      patchState(store, { searchResults: [] });
    },

    registerDevice(body: DeviceRegistrationRequest): void {
      patchState(store, { loading: true, error: null });
      certificateService
        .registerDevice(body)
        .pipe(take(1))
        .subscribe({
          next: (registrationResponse) => {
            patchState(store, { registrationResponse, loading: false });
          },
          error: (error: unknown) => {
            patchState(store, { error, loading: false });
          },
        });
    },

    confirmAssociation(
      confirmationPin: string,
      body: DeviceCertificateApprovalBody,
    ): void {
      patchState(store, { loading: true, error: null });
      certificateService
        .confirmAssociation(confirmationPin, body)
        .pipe(take(1))
        .subscribe({
          next: () => {
            patchState(store, { associationConfirmed: true, loading: false });
          },
          error: (error: unknown) => {
            patchState(store, { error, loading: false });
          },
        });
    },

    authorizeDevice(body: ConfirmationRequest): void {
      patchState(store, { loading: true, error: null });
      certificateService
        .authorizeDevice(body)
        .pipe(take(1))
        .subscribe({
          next: (authorizationResponse) => {
            patchState(store, { authorizationResponse, loading: false });
          },
          error: (error: unknown) => {
            patchState(store, { error, loading: false });
          },
        });
    },

    clearError(): void {
      patchState(store, { error: null });
    },

    reset(): void {
      patchState(store, initialState);
    },
  })),
);
